import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Control, SubmitHandler } from 'react-hook-form';

import {
  createStudentAssessment,
  StudentAssessmentBody,
  updateStudentAssessment,
} from '@/data/services/studentAssessmentsServices';
import {
  OccurrenceAssessment,
  StudentAssessment,
} from '@/models/StudentAssessment';
import { buildChangedObject } from '@/utils/buildChangedObject';
import OccurrenceAssessmentForm from './OccurrenceAssessmentForm';
import { SheetTab } from '@/models/SheetTab';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';

type OccurrenceAssessmentServiceProps = {
  assessmentControl?: Control<StudentAssessment>;
  pedagogicalSheetId: number;
  studentAssessment?: StudentAssessment;
  updateAssessments?: () => Promise<void>;
  changeTab?: (tab: SheetTab) => void;
};

export default function OccurrenceAssessmentService(
  props: OccurrenceAssessmentServiceProps,
) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'occurrenceAssessmentForm',
  });

  const {
    studentAssessment: defaultValues,
    pedagogicalSheetId,
    updateAssessments,
    changeTab,
  } = props;

  const createMode = !defaultValues;

  const onSuccessAction = async () => {
    if (updateAssessments) await updateAssessments();
    changeTab?.('view');
  };

  const { mutate: create, isLoading: loadingCreate } = useMutation(
    createStudentAssessment,
    {
      async onSuccess() {
        alert.success(t('successCreatedMessage'));
        await onSuccessAction();
      },
      onError(error: any) {
        alert.error(getErrorMessage(error));
      },
    },
  );

  const { mutate: update, isLoading: loadingUpdate } = useMutation(
    updateStudentAssessment,
    {
      async onSuccess() {
        alert.success(t('successUpdatedMessage'));
        await onSuccessAction();
      },
      onError(error: any) {
        alert.error(getErrorMessage(error));
      },
    },
  );

  const onSubmitUpdate: SubmitHandler<StudentAssessment> = async data => {
    if (!defaultValues?.id) throw new Error('assessment not found');

    if (defaultValues) {
      const assessmentId = defaultValues.id;

      const assessmentChanges = buildChangedObject<StudentAssessment>(
        defaultValues,
        data,
      );

      const occurrenceChanges = buildChangedObject<OccurrenceAssessment>(
        defaultValues.occurrenceAssessment,
        data.occurrenceAssessment,
      );

      const body = {
        ...assessmentChanges,
        occurrenceAssessment: occurrenceChanges,
      };
      update({ assessmentId, body });
    }
  };

  const onSubmitCreate: SubmitHandler<StudentAssessment> = async ({
    occurrenceAssessment,
  }: StudentAssessment) => {
    if (!pedagogicalSheetId) throw new Error('pedagogical sheet not found');

    const assessment: StudentAssessmentBody = {
      pedagogicalSheetId,
      occurrenceAssessment,
    };

    create(assessment);
  };

  const onSubmit = createMode ? onSubmitCreate : onSubmitUpdate;

  const loading = loadingCreate || loadingUpdate;

  return (
    <OccurrenceAssessmentForm
      {...props}
      onSubmit={onSubmit}
      loading={loading}
      onCancel={() => changeTab?.('view')}
    />
  );
}
